import Button from '@/components/buttons/button/button.component'
import { ModalStatusTypes, SocialNetworksAvailables } from '@/interfaces/socialnetworks.interface'
import { Identity } from '@/interfaces/user.interface'
import DataLayerHelper from '@helpers/data-layer.helper'
import localForageHelper from '@helpers/localforage.helper'
import Environment from '@interfaces/environment.interface'
import User from '@interfaces/user.interface'
import onEnvironmentChange from '@observables/environment.observable'
import onUserChange from '@observables/user.observable'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'

const STORAGE_URL = 'web-hub/images/social-network'

export interface Props {
  onCloseModal: () => void
  onSetModalStatus?: (status: ModalStatusTypes) => void
  onAddAccount: (network: SocialNetworksAvailables) => void
  initialStepCode?: Code
  notBusinessProfile?: boolean
}

interface Controller {
  code?: string
  type: ActionType
  linkUrl?: string
  label: LabelElement
}

export type LabelElement = string | JSX.Element | Array<string | JSX.Element>
export type ActionType = 'confirm' | 'cancel' | 'link'
export type Code =
  | 'HAS_BUSINESS_ACCOUNT'
  | 'HOW_CHANGE_BUSINESS_ACCOUNT'
  | 'HAS_FACEBOOK_PAGE'
  | 'HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM'
  | 'NOW_LETS_CONNECT_TO_FACEBOOK'

export interface Step {
  code?: string
  page?: number
  description: string
  message?: LabelElement
  steps: Array<LabelElement>
  cancel?: Controller
  confirm?: Controller
  prev?: Code
}

const useNewTutorialInstagram = ({ onCloseModal, onSetModalStatus, initialStepCode, onAddAccount, notBusinessProfile }: Props) => {
  const { t, i18n } = useTranslation('modalVinculateSocialNetwork')
  const [environments, setEnvironments] = useState<Environment | undefined>()
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Identity | null>(null)
  const [action, setAction] = useState<'addAccount' | 'renew' | undefined>(undefined)
  const lang = useMemo(() => i18n?.language?.split('-')?.[0], [i18n])
  const dataLayerHelper = useMemo(() => new DataLayerHelper(), [])

  // PÁGINAS DO MODAL
  const HAS_BUSINESS_ACCOUNT: Step = useMemo(
    () => ({
      code: 'HAS_BUSINESS_ACCOUNT',
      page: 1,
      description: t('newTutorialInstagram.HAS_BUSINESS_ACCOUNT.description'),
      steps: [
        <p
          dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HAS_BUSINESS_ACCOUNT.timeline.firstDot') }}
          key="HAS_BUSINESS_ACCOUNT-1"
        />,
        <p
          dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HAS_BUSINESS_ACCOUNT.timeline.secondDot') }}
          key="HAS_BUSINESS_ACCOUNT-2"
        />,
        [
          <p
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HAS_BUSINESS_ACCOUNT.timeline.thirdDot') }}
            key="HAS_BUSINESS_ACCOUNT-3"
          />,
          <img
            src={`${environments?.exteralApis?.gtorage}/${STORAGE_URL}/${lang}/HAS_BUSINESS_ACCOUNT_IMAGE.png`}
            alt="Step"
            className="mt-3"
            width="70%"
            key="HAS_BUSINESS_ACCOUNT-4"
          />,
        ],
      ],
      cancel: {
        type: 'cancel',
        label: (
          <span
            id="social-network-back-tutorial-page-HAS_BUSINESS_ACCOUNT"
            style={{ fontSize: '0.9rem' }}
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HAS_BUSINESS_ACCOUNT.backButtonLabel') }}
            key="HAS_BUSINESS_ACCOUNT-5"
          />
        ),
        code: 'HOW_CHANGE_BUSINESS_ACCOUNT',
      },
      confirm: {
        code: 'HAS_FACEBOOK_PAGE',
        type: 'confirm',
        label: (
          <span
            id="social-network-next-tutorial-page-HAS_BUSINESS_ACCOUNT"
            style={{ fontSize: '0.9rem' }}
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HAS_BUSINESS_ACCOUNT.nextButtonLabel') }}
            key="HAS_BUSINESS_ACCOUNT-6"
          />
        ),
      },
    }),
    [environments?.exteralApis?.gtorage, lang, t]
  )

  const HOW_CHANGE_BUSINESS_ACCOUNT: Step = useMemo(
    () => ({
      code: 'HOW_CHANGE_BUSINESS_ACCOUNT',
      page: 1,
      description: t('newTutorialInstagram.HOW_CHANGE_BUSINESS_ACCOUNT.description'),
      steps: [
        <p
          dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HOW_CHANGE_BUSINESS_ACCOUNT.timeline.firstDot') }}
          key="HOW_CHANGE_BUSINESS_ACCOUNT-1"
        />,
        <p
          dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HOW_CHANGE_BUSINESS_ACCOUNT.timeline.secondDot') }}
          key="HOW_CHANGE_BUSINESS_ACCOUNT-2"
        />,
        <p
          dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HOW_CHANGE_BUSINESS_ACCOUNT.timeline.thirdDot') }}
          key="HOW_CHANGE_BUSINESS_ACCOUNT-3"
        />,
        [
          <p
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HOW_CHANGE_BUSINESS_ACCOUNT.timeline.fourthDot') }}
            key="HOW_CHANGE_BUSINESS_ACCOUNT-4"
          />,
          <img
            src={`${environments?.exteralApis?.gtorage}/${STORAGE_URL}/${lang}/HOW_CHANGE_BUSINESS_ACCOUNT_IMAGE.png`}
            alt="Step"
            className="mt-3"
            width="80%"
            key="HOW_CHANGE_BUSINESS_ACCOUNT-6"
          />,
        ],
      ],
      cancel: {
        type: 'link',
        label: (
          <span
            id="social-network-link-tutorial-page-HOW_CHANGE_BUSINESS_ACCOUNT"
            style={{ fontSize: '0.9rem' }}
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HOW_CHANGE_BUSINESS_ACCOUNT.backLinkLabel') }}
            key="HOW_CHANGE_BUSINESS_ACCOUNT-7"
          />
        ),
        linkUrl: `${environments?.squidProducts?.creators}/docs/como-ter-um-perfil-creatorbusiness-no-instagram-e-uma-pagina-do-facebook-vinculada`,
      },
      confirm: {
        code: 'HAS_FACEBOOK_PAGE',
        type: 'confirm',
        label: (
          <span
            id="social-network-next-tutorial-page-HOW_CHANGE_BUSINESS_ACCOUNT"
            style={{ fontSize: '0.9rem' }}
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HOW_CHANGE_BUSINESS_ACCOUNT.nextButtonLabel') }}
            key="HOW_CHANGE_BUSINESS_ACCOUNT-8"
          />
        ),
      },
      prev: 'HAS_BUSINESS_ACCOUNT',
    }),
    [environments?.exteralApis?.gtorage, environments?.squidProducts?.creators, lang, t]
  )

  const HAS_FACEBOOK_PAGE: Step = useMemo(
    () => ({
      code: 'HAS_FACEBOOK_PAGE',
      page: 2,
      description: t('newTutorialInstagram.HAS_FACEBOOK_PAGE.description'),
      steps: [
        <p dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HAS_FACEBOOK_PAGE.timeline.firstDot') }} key="HAS_FACEBOOK_PAGE-1" />,
        <p
          dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HAS_FACEBOOK_PAGE.timeline.secondDot') }}
          key="HAS_FACEBOOK_PAGE-2"
        />,
        <p dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HAS_FACEBOOK_PAGE.timeline.thirdDot') }} key="HAS_FACEBOOK_PAGE-3" />,
        [
          <p
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HAS_FACEBOOK_PAGE.timeline.fourthDot') }}
            key="HAS_FACEBOOK_PAGE-4"
          />,
          <img
            src={`${environments?.exteralApis?.gtorage}/${STORAGE_URL}/${lang}/HAS_FACEBOOK_PAGE_IMAGE.png`}
            alt="Step"
            className="mt-3"
            width="70%"
            key="HAS_FACEBOOK_PAGE-5"
          />,
        ],
      ],
      confirm: {
        code: 'NOW_LETS_CONNECT_TO_FACEBOOK',
        type: 'confirm',
        label: (
          <span
            id="social-network-next-tutorial-page-HAS_FACEBOOK_PAGE"
            style={{ fontSize: '0.9rem' }}
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HAS_FACEBOOK_PAGE.nextButtonLabel') }}
            key="HAS_FACEBOOK_PAGE-6"
          />
        ),
      },
      cancel: {
        code: 'HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM',
        type: 'cancel',
        label: (
          <span
            id="social-network-back-tutorial-page-HAS_FACEBOOK_PAGE"
            style={{ fontSize: '0.9rem' }}
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HAS_FACEBOOK_PAGE.backButtonLabel') }}
            key="HAS_FACEBOOK_PAGE-7"
          />
        ),
      },
      prev: 'HAS_BUSINESS_ACCOUNT',
    }),
    [environments?.exteralApis?.gtorage, lang, t]
  )

  const HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM: Step = useMemo(
    () => ({
      code: 'HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM',
      page: 2,
      description: t('newTutorialInstagram.HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM.description'),
      steps: [
        <p
          dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM.timeline.firstDot') }}
          key="HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM-1"
        />,
        <p
          dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM.timeline.secondDot') }}
          key="HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM-2"
        />,
        [
          <p
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM.timeline.thirdDot') }}
            key="HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM-3"
          />,
          <img
            src={`${environments?.exteralApis?.gtorage}/${STORAGE_URL}/${lang}/HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM_IMAGE.png`}
            alt="Step"
            className="mt-3"
            width="70%"
            key="HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM-4"
          />,
        ],
      ],
      cancel: {
        type: 'link',
        linkUrl: `${environments?.squidProducts?.creators}/docs/não-consigo-conectar-uma-página-do-facebook-ao-meu-instagram-o-que-pode-ser`,
        label: (
          <span
            id="social-network-link-tutorial-page-HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM"
            style={{ fontSize: '0.9rem' }}
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM.backLinkLabel') }}
            key="HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM-5"
          />
        ),
      },
      confirm: {
        code: 'NOW_LETS_CONNECT_TO_FACEBOOK',
        type: 'confirm',
        label: (
          <span
            id="social-network-next-tutorial-page-HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM"
            style={{ fontSize: '0.9rem' }}
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM.nextButtonLabel') }}
            key="HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM-5"
          />
        ),
      },
      prev: 'HAS_FACEBOOK_PAGE',
    }),
    [environments?.exteralApis?.gtorage, environments?.squidProducts?.creators, lang, t]
  )

  const NOW_LETS_CONNECT_TO_FACEBOOK: Step = useMemo(
    () => ({
      code: 'NOW_LETS_CONNECT_TO_FACEBOOK',
      page: 3,
      description: t('newTutorialInstagram.NOW_LETS_CONNECT_TO_FACEBOOK.description'),
      message: <p dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.NOW_LETS_CONNECT_TO_FACEBOOK.message') }} />,
      steps: [
        <p
          dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.NOW_LETS_CONNECT_TO_FACEBOOK.timeline.firstDot') }}
          key="NOW_LETS_CONNECT_TO_FACEBOOK-1"
        />,
        <p
          dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.NOW_LETS_CONNECT_TO_FACEBOOK.timeline.secondDot') }}
          key="NOW_LETS_CONNECT_TO_FACEBOOK-2"
        />,
        <p
          dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.NOW_LETS_CONNECT_TO_FACEBOOK.timeline.thirdDot') }}
          key="NOW_LETS_CONNECT_TO_FACEBOOK-3"
        />,
        <p
          dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.NOW_LETS_CONNECT_TO_FACEBOOK.timeline.fourthDot') }}
          key="NOW_LETS_CONNECT_TO_FACEBOOK-4"
        />,
      ],
      cancel: {
        type: 'link',
        linkUrl: `${environments?.squidProducts?.creators}/docs/conexão-de-rede-social`,
        label: (
          <span
            id="social-network-link-tutorial-page-NOW_LETS_CONNECT_TO_FACEBOOK"
            style={{ fontSize: '0.9rem' }}
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.NOW_LETS_CONNECT_TO_FACEBOOK.backLinkLabel') }}
            key="NOW_LETS_CONNECT_TO_FACEBOOK-5"
          />
        ),
      },
      confirm: {
        type: 'confirm',
        label: [
          <span
            id="social-network-next-tutorial-page-NOW_LETS_CONNECT_TO_FACEBOOK"
            key="NOW_LETS_CONNECT_TO_FACEBOOK-6"
            style={{ fontSize: '0.9rem' }}
            dangerouslySetInnerHTML={{ __html: t('newTutorialInstagram.NOW_LETS_CONNECT_TO_FACEBOOK.nextButtonLabel') }}
          />,
          <i className="fa-light fa-arrow-up-right-from-square ml-2 small" key="NOW_LETS_CONNECT_TO_FACEBOOK-7" />,
        ],
      },
      prev: 'HAS_FACEBOOK_PAGE',
    }),
    [t, environments?.squidProducts?.creators]
  )

  const STEPS: Array<Step> = useMemo(
    () => [
      HAS_BUSINESS_ACCOUNT,
      HOW_CHANGE_BUSINESS_ACCOUNT,
      HAS_FACEBOOK_PAGE,
      HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM,
      NOW_LETS_CONNECT_TO_FACEBOOK,
    ],
    [
      HAS_BUSINESS_ACCOUNT,
      HAS_FACEBOOK_PAGE,
      HOW_CHANGE_BUSINESS_ACCOUNT,
      HOW_CONNECT_FACEBOOK_PAGE_TO_INSTAGRAM,
      NOW_LETS_CONNECT_TO_FACEBOOK,
    ]
  )
  const maxStep = useRef(STEPS?.reduce((acc, step) => (step?.page > acc ? step?.page : acc), 0))
  // FIM PÁGINAS DO MODAL

  const [width, setWidth] = useState(window.innerWidth)
  const [height, setHeight] = useState(window.innerHeight)
  const [activeStep, setActiveStep] = useState<Step | null>(null)

  const handleCloseModal = useCallback(async () => {
    if (onCloseModal && typeof onCloseModal === 'function') {
      await dataLayerHelper?.sendToDataLayer({
        event: 'tutorial_action',
        scope: 'Btn - redes sociais | Facebook',
        action: 'abandoned',
        description: 'Close',
        step: `Etapa ${activeStep?.page}-1`,
        nextStep: '/social-networks',
      })
      onCloseModal()
    }
  }, [activeStep?.page, onCloseModal, dataLayerHelper])

  const handlePreviousButtonClick = useCallback(async () => {
    await dataLayerHelper?.sendToDataLayer({
      event: 'tutorial_action',
      scope: 'Btn - redes sociais | Facebook',
      action: 'prev',
      description: 'Voltar',
      step: `Etapa ${activeStep?.page}-1`,
      nextStep: 'Conexão de rede social',
    })
    if (activeStep?.prev) {
      setActiveStep(STEPS?.find((st) => st?.code === activeStep?.prev))
    } else if (onSetModalStatus && typeof onSetModalStatus === 'function') {
      onSetModalStatus('username-to-connect')
    }
  }, [activeStep?.prev, onSetModalStatus, STEPS, activeStep?.page, dataLayerHelper])

  const getReadableText = useCallback((inputValue) => {
    if (inputValue?.props?.dangerouslySetInnerHTML.__html) {
      return inputValue?.props?.dangerouslySetInnerHTML.__html
    }

    if (inputValue && typeof inputValue === 'string') {
      return inputValue
    }

    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = inputValue

    return tempDiv?.textContent ?? tempDiv?.innerText ?? ''
  }, [])

  const handleCancelButtonClick = useCallback(async () => {
    await dataLayerHelper?.sendToDataLayer({
      event: 'tutorial_action',
      scope: 'Btn - redes sociais | Facebook',
      action: 'Stop',
      description: getReadableText(activeStep?.cancel?.label),
      step: `Etapa ${activeStep?.page}-1`,
      nextStep: activeStep?.page > 1 ? `Etapa ${activeStep?.page - 1}` : '/social-networks',
    })
    if (activeStep?.cancel?.code) {
      setActiveStep(STEPS?.find((st) => st?.code === activeStep?.cancel?.code))
    } else {
      onCloseModal()
    }
  }, [onCloseModal, activeStep?.cancel?.code, STEPS, activeStep?.cancel?.label, activeStep?.page, dataLayerHelper, getReadableText])

  const onRenewToken = useCallback(
    async (idProfile: string) => {
      await localForageHelper.setItem('actions', { action: 'renew', idProfile })
      window.location.href = `${environments?.squidApis?.proxy}/facebook?squid-callback=${window.location.href}`
    },
    [environments?.squidApis?.proxy]
  )

  const handleNextButtonClick = useCallback(async () => {
    await dataLayerHelper?.sendToDataLayer({
      event: 'tutorial_action',
      scope: 'Btn - redes sociais | Facebook',
      action: 'next',
      description: getReadableText(activeStep?.confirm?.label),
      step: `Etapa ${activeStep?.page}`,
      nextStep: activeStep?.page < maxStep?.current ? `Etapa ${activeStep?.page + 1}` : null,
    })

    if (activeStep?.page === maxStep.current) {
      await dataLayerHelper?.sendToDataLayer({
        event: 'tutorial_complete',
        context: 'Conexão | Facebook',
        step: `Etapa ${activeStep?.page} de ${maxStep.current}`,
        description: 'coleta click para finalizar tutorial',
      })
    }

    if (activeStep?.confirm?.code) {
      setActiveStep(STEPS?.find((st) => st?.code === activeStep?.confirm?.code))
    } else {
      let action
      const { action: actionFromLocalStorage, idProfile } =
        ((await localForageHelper.getItem('actions')) as { action: 'addAccount' | 'renew'; idProfile?: string }) || {}
      action = actionFromLocalStorage
      const idProfileRenewed = (await localForageHelper.getItem('idProfileRenewed')) as string
      if (!action && idProfileRenewed) {
        action = 'renew'
      }

      if ((!action && !idProfileRenewed) || action === 'addAccount') {
        onSetModalStatus('profile-category')
        onAddAccount('facebook')
        onCloseModal()
      } else if (action === 'renew') {
        await onRenewToken(idProfile ?? idProfileRenewed)
      }
    }
  }, [
    STEPS,
    activeStep?.confirm?.code,
    onSetModalStatus,
    onAddAccount,
    onCloseModal,
    onRenewToken,
    activeStep?.confirm?.label,
    activeStep?.page,
    dataLayerHelper,
    getReadableText,
  ])

  const getConfirmElement = useCallback(() => {
    return (
      <Button
        width="100%"
        size="sm"
        inverted={activeStep?.confirm?.type === 'cancel'}
        id={activeStep?.confirm?.code}
        onClick={async () => await handleNextButtonClick()}
      >
        {activeStep?.confirm?.label}
      </Button>
    )
  }, [handleNextButtonClick, activeStep?.confirm?.label, activeStep?.confirm?.type, activeStep?.confirm?.code])

  const getCancelElement = useCallback(() => {
    if (['confirm', 'cancel'].includes(activeStep?.cancel?.type)) {
      return (
        <Button
          width="100%"
          size="sm"
          inverted={activeStep?.cancel?.type === 'cancel' || !activeStep?.cancel?.code}
          id={activeStep?.cancel?.code}
          onClick={() => handleCancelButtonClick()}
        >
          {activeStep?.cancel?.label}
        </Button>
      )
    } else if (activeStep?.cancel?.type === 'link') {
      return (
        <a href={activeStep?.cancel?.linkUrl} target="_blank" className="text-center small" id={activeStep?.cancel?.code}>
          {activeStep?.cancel?.label}
        </a>
      )
    }
  }, [handleCancelButtonClick, activeStep?.cancel?.linkUrl, activeStep?.cancel?.code, activeStep?.cancel?.label, activeStep?.cancel?.type])

  useEffect(() => {
    setActiveStep(STEPS?.find((st) => st?.code === initialStepCode) || STEPS?.find((st) => st?.code === 'HAS_BUSINESS_ACCOUNT'))
  }, [STEPS, initialStepCode])

  useEffect(() => {
    const onEnvironmentSubscription: SubscriptionLike = onEnvironmentChange?.subscribe((environment) => {
      setEnvironments(environment)
    })

    return () => onEnvironmentSubscription?.unsubscribe()
  }, [])

  useEffect(() => {
    const getIdentityByIdProfile = async (idProfile: string) => {
      return user?.identities?.find((identity) => identity.id === idProfile)
    }

    const checkProfile = async () => {
      const { action, idProfile } =
        ((await localForageHelper.getItem('actions')) as { action: 'addAccount' | 'renew'; idProfile?: string }) || {}
      setAction(action)
      if (action === 'renew' && idProfile) {
        const identity = await getIdentityByIdProfile(idProfile)
        setProfile(identity)
      }
    }

    if (user) {
      checkProfile()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    const onUserChangeSubscription: SubscriptionLike = onUserChange.subscribe((userChanged) => {
      setUser(userChanged)
    })

    return () => onUserChangeSubscription?.unsubscribe()
  }, [])

  useLayoutEffect(() => {
    const updateSize = () => {
      setWidth(window.innerWidth)
      setHeight(window.innerHeight)
    }
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  return {
    width,
    height,
    activeStep,
    maxStep: maxStep.current,
    notBusinessProfile,
    profile,
    action,
    handleCloseModal,
    getConfirmElement,
    getCancelElement,
    handlePreviousButtonClick,
    onSetModalStatus,
  }
}

export default useNewTutorialInstagram
